'use client';

import { useState, startTransition, useCallback } from 'react';

import Link from 'next/link';
import { clsx } from 'clsx';

import { Icons, IconButton } from '@monoimin/atoms';
import { NEW_ICON_LINK_ICONS } from '@monoimin/constants';

import { LocaleSwitcher } from './LocaleSwitcher';

import type { LinksCollectionStoryblok } from '@monoimin/sb-types';

import type { MenuPanelProps } from '../HeaderCompact';

export const ShiftMenuPanel = ({ blok, localesList }: MenuPanelProps) => {
  const { links, contactLinks } = blok;

  const withLocales = Array.isArray(localesList) && localesList.length > 0;

  const withContacts = Array.isArray(contactLinks) && contactLinks.length > 0;

  const [showSubmenu, setShowSubmenu] = useState(false);

  const [collection, setCollection] = useState<LinksCollectionStoryblok | null>(
    () => {
      return links?.find((item) => item.collections?.length) ?? null;
    }
  );

  const onMenuItemClick = useCallback(
    (collection: LinksCollectionStoryblok) => {
      startTransition(() => {
        setShowSubmenu(true);
        setCollection(collection);
      });
    },
    []
  );

  return (
    <nav className='z-10 flex h-screen w-full pt-[68px]'>
      <div
        className={clsx(
          'relative col-span-full flex h-full w-full flex-col overflow-y-scroll bg-blue-500 pb-16 duration-[350ms]',
          showSubmenu ? '-left-full' : 'left-0'
        )}
      >
        {Array.isArray(links) && links.length > 0 && (
          <ul className='flex flex-col'>
            {links.map((item) => {
              const { _uid, title, links, mainLink } = item;

              const haveLink =
                mainLink && Boolean(mainLink?.url && mainLink?.title);

              const withSubmenu = Array.isArray(links) && links.length > 0;

              return (
                <li
                  key={_uid}
                  className='flex items-center text-lg font-bold text-white duration-200 xl:min-h-[64px] xl:text-xl	 xl:leading-5 xl:hover:bg-[#00155A]'
                >
                  {haveLink && (
                    <Link
                      href={mainLink.url}
                      className='button block h-full w-full px-6 py-3 xl:px-9 xl:py-5'
                    >
                      {mainLink.title}
                    </Link>
                  )}
                  {withSubmenu && (
                    <IconButton
                      type='button'
                      className='button flex w-full flex-row-reverse items-center justify-between gap-3 px-6 py-3 text-left xl:px-9 xl:py-5'
                      onClick={() => {
                        onMenuItemClick(item);
                      }}
                      iconProps={{
                        icon: 'dropdown',
                        sizeClassName: 'lg:h-6 lg:w-6 h-3 w-3',
                        className: '-rotate-90 text-white stroke-[0.4px]',
                        isInteractive: true,
                      }}
                    >
                      <p className='truncate whitespace-nowrap'>{title}</p>
                    </IconButton>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {(withLocales || withContacts) && (
          <div className='mx-6 my-4 flex flex-col gap-32 border-t border-white/30 py-8 xl:hidden'>
            {withLocales && (
              <LocaleSwitcher
                localesList={localesList}
                className='w-fit text-white'
                optionsClassName='left-0 mt-2'
                withActiveLocaleText
              />
            )}
            {withContacts && (
              <ul className='flex flex-wrap gap-5'>
                {contactLinks.map((item) => {
                  const { _uid, link, icon } = item;

                  const iconName = icon && NEW_ICON_LINK_ICONS[icon];

                  return (
                    <li key={_uid}>
                      <Link
                        href={link?.url}
                        target={link?.target}
                        className='button flex items-center gap-1 text-lg text-white'
                      >
                        {iconName && (
                          <Icons icon={iconName} className='h-5 w-5' />
                        )}
                        {link?.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
      <div
        className={clsx(
          'bg-grey absolute z-20 h-full w-full touch-pan-y overflow-y-scroll pb-16 duration-[350ms] xl:static xl:left-auto xl:col-span-9 xl:w-auto xl:overflow-y-auto xl:pb-6 xl:pt-14',
          showSubmenu ? 'left-0' : 'left-full'
        )}
      >
        {Boolean(collection?.title) && (
          <IconButton
            type='button'
            className='button border-line col-span-full flex w-full items-center gap-3 border-b px-6 py-5 xl:hidden'
            onClick={() => {
              setShowSubmenu(false);
            }}
            iconProps={{
              icon: 'dropdown',
              className: 'rotate-90',
              isInteractive: true,
            }}
          >
            <span className='text-xl font-bold leading-6'>
              {collection?.title}
            </span>
          </IconButton>
        )}
        {collection &&
          Array.isArray(collection.links) &&
          collection.links.length > 0 && (
            <ul className='flex flex-col gap-3 px-6 py-5'>
              {collection.links.map((item) => {
                const { _uid, link } = item;

                return (
                  <li key={_uid}>
                    <Link href={link?.url}>{link?.title}</Link>
                  </li>
                );
              })}
            </ul>
          )}
      </div>
    </nav>
  );
};
